import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEventTheme } from '../context/ThemeContext';
import { 
  House, 
  Users, 
  CalendarCheck, 
  Gift, 
  Truck, 
  ListChecks,
  SignOut,
  Clock,
  List,
  ArrowLeft,
  ArrowsLeftRight,
  CaretDown,
  CaretUpDown,
  Camera,
  Phone,
  Ticket,
  TShirt,
  HeartStraight,
  Plus,
  Check,
  MapPin,
  NavigationArrow,
  Palette,
  Pencil,
  Gear,
  Star,
  Wallet,
  PushPin,
  PushPinSimple,
  UserCircle,
  InstagramLogo,
  PuzzlePiece
} from '@phosphor-icons/react';
import RoomBedIcon from './icons/RoomBedIcon';
import { cn } from '../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import api from '../services/api';
import ChatWidget from './ChatWidget';
import { toast } from 'sonner';
import useEventTypeConfig from '../hooks/useEventTypeConfig';
import {
  MAX_PINNED_SIDEBAR_LINKS,
  orderSidebarLinks,
  readPinnedSidebarLinks,
  savePinnedSidebarLinks,
} from '../lib/sidebarPins';
import { isWithinEventPeriod } from '../lib/eventPeriod';

const eventManagerLinks = [
  { to: '/dashboard', icon: ListChecks, label: 'Dashboard' },
  { to: '/dashboard/itinerary', icon: CalendarCheck, label: 'Itinerary', featureKey: 'showItinerary' },
  { to: '/dashboard/guests', icon: Users, label: 'Guests' },
  { to: '/dashboard/games', icon: PuzzlePiece, label: 'Celebration Activities', labelKey: 'celebrationStudioTitle', featureKey: 'showCelebrationGames' },
  { to: '/dashboard/rooms', icon: RoomBedIcon, label: 'Rooms', featureKey: 'showAccommodation' },
  { to: '/dashboard/tasks', icon: ListChecks, label: 'Tasks' },
  { to: '/dashboard/gifts', icon: Gift, label: 'Registry', labelKey: 'registryTitle', featureKey: 'showWishlist' },
  { to: '/dashboard/vendors', icon: Truck, label: 'Vendors' },
  { to: '/dashboard/budget', icon: Wallet, label: 'Budget' },
  { to: '/dashboard/contacts', icon: Phone, label: 'Key Contacts' },
  { to: '/dashboard/photos', icon: Camera, label: 'Photos' },
  { to: '/dashboard/social', icon: InstagramLogo, label: 'Social Studio', featureKey: 'showSocialStudio' },
  { to: '/dashboard/theme', icon: Palette, label: 'Theme & Invite Studio', featureKey: 'showInviteStudio' },
  { to: '/dashboard/showcase', icon: Star, label: 'Showcase' },
];

const guestLinks = [
  { to: '/guest', icon: House, label: 'Event Home' },
  { to: '/guest/tickets', icon: Ticket, label: 'My Tickets & ID', featureKey: 'showTravelAndId' },
  { to: '/guest/room', icon: RoomBedIcon, label: 'My Room', featureKey: 'showAccommodation' },
  { to: '/guest/itinerary', icon: CalendarCheck, label: 'Itinerary', featureKey: 'showItinerary' },
  { to: '/guest/wardrobe', icon: TShirt, label: 'Wardrobe', featureKey: 'showWardrobe' },
  { to: '/guest/photos', icon: Camera, label: 'Photos' },
  { to: '/guest/gifts', icon: Gift, label: 'Registry', labelKey: 'registryTitle', featureKey: 'showWishlist' },
  { to: '/guest/rsvp', icon: HeartStraight, label: 'RSVP' },
  { to: '/guest/contacts', icon: Phone, label: 'Key Contacts' },
];

const adminGuestLinks = [
  { to: '/guest', icon: House, label: 'Event Home' },
  { to: '/dashboard/rooms', icon: RoomBedIcon, label: 'Manage Rooms', featureKey: 'showAccommodation' },
  { to: '/guest/tickets', icon: Ticket, label: 'My Tickets & ID', featureKey: 'showTravelAndId' },
  { to: '/guest/room', icon: RoomBedIcon, label: 'My Room', featureKey: 'showAccommodation' },
  { to: '/guest/itinerary', icon: CalendarCheck, label: 'Itinerary', featureKey: 'showItinerary' },
  { to: '/guest/wardrobe', icon: TShirt, label: 'Wardrobe', featureKey: 'showWardrobe' },
  { to: '/guest/photos', icon: Camera, label: 'Photos' },
  { to: '/guest/gifts', icon: Gift, label: 'Registry', labelKey: 'registryTitle', featureKey: 'showWishlist' },
  { to: '/guest/rsvp', icon: HeartStraight, label: 'RSVP' },
  { to: '/guest/contacts', icon: Phone, label: 'Key Contacts' },
];

const coupleLinks = [
  { to: '/couple', icon: House, label: 'Dashboard' },
  { to: '/couple/guests', icon: Users, label: 'Guests' },
  { to: '/dashboard/itinerary', icon: CalendarCheck, label: 'Itinerary', featureKey: 'showItinerary' },
  { to: '/dashboard/games', icon: PuzzlePiece, label: 'Celebration Activities', labelKey: 'celebrationStudioTitle', featureKey: 'showCelebrationGames' },
  { to: '/couple/rooms', icon: RoomBedIcon, label: 'Rooms', featureKey: 'showAccommodation' },
  { to: '/couple/photos', icon: Camera, label: 'Photos' },
  { to: '/dashboard/social', icon: InstagramLogo, label: 'Social Studio', featureKey: 'showSocialStudio' },
  { to: '/dashboard/theme', icon: Palette, label: 'Theme & Invite Studio', featureKey: 'showInviteStudio' },
  { to: '/couple/gifts', icon: Gift, label: 'Registry', labelKey: 'registryTitle', featureKey: 'showWishlist' },
  { to: '/couple/vendors', icon: Truck, label: 'Vendors' },
  { to: '/couple/tasks', icon: ListChecks, label: 'Tasks' },
];

const teamMemberLinks = [
  { to: '/dashboard/tasks', icon: ListChecks, label: 'My Tasks' },
  { to: '/dashboard/budget?tab=spend', icon: Wallet, label: 'My Expenses' },
];

const hostLinks = [
  { to: '/host', icon: House, label: 'Dashboard' },
  { to: '/host/guests', icon: Users, label: 'Guests' },
  { to: '/dashboard/itinerary', icon: CalendarCheck, label: 'Itinerary', featureKey: 'showItinerary' },
  { to: '/dashboard/games', icon: PuzzlePiece, label: 'Celebration Activities', labelKey: 'celebrationStudioTitle', featureKey: 'showCelebrationGames' },
  { to: '/host/rooms', icon: RoomBedIcon, label: 'Rooms', featureKey: 'showAccommodation' },
  { to: '/host/photos', icon: Camera, label: 'Photos' },
  { to: '/dashboard/social', icon: InstagramLogo, label: 'Social Studio', featureKey: 'showSocialStudio' },
  { to: '/dashboard/theme', icon: Palette, label: 'Theme & Invite Studio', featureKey: 'showInviteStudio' },
  { to: '/host/gifts', icon: Gift, label: 'Registry', labelKey: 'registryTitle', featureKey: 'showWishlist' },
  { to: '/host/vendors', icon: Truck, label: 'Vendors' },
  { to: '/host/tasks', icon: ListChecks, label: 'Tasks' },
];

const hotelManagerLinks = [
  { to: '/dashboard/rooms?tab=arrivals', icon: RoomBedIcon, label: 'Arrivals' },
  { to: '/dashboard/rooms?tab=rooms', icon: RoomBedIcon, label: 'Rooms' },
];

const settingsNavLink = { to: '/dashboard/settings', icon: Gear, label: 'Settings' };
const eventManagerPinPaths = [...eventManagerLinks, settingsNavLink]
  .filter((link) => link.to !== '/dashboard')
  .map((link) => link.to);

const filterLinksByEventType = (links, eventTypeConfig) => (
  links
    .filter((link) => !link.featureKey || eventTypeConfig.features?.[link.featureKey])
    .map((link) => link.labelKey && eventTypeConfig.labels?.[link.labelKey]
      ? { ...link, label: eventTypeConfig.labels[link.labelKey] }
      : link)
);

const formatViewerRole = (role) => {
  if (role === 'manager') return 'Managing';
  if (role === 'staff') return 'Staff';
  if (role === 'hotel_manager') return 'Hotel';
  if (role === 'admin_guest') return 'Admin Guest';
  if (role === 'bride') return 'Bride';
  if (role === 'groom') return 'Groom';
  if (role === 'host') return 'Host';
  return 'Attending';
};

const getManagerBranding = (currentEvent, user, isEventManager) => {
  const eventBranding = currentEvent?.manager_branding || {};
  if (!isEventManager) return currentEvent?.manager_branding;

  const branding = {
    company_name: user?.company_name || eventBranding.company_name,
    company_logo: user?.company_logo || eventBranding.company_logo,
    company_website: user?.company_website || eventBranding.company_website,
  };

  return branding.company_name || branding.company_logo || branding.company_website ? branding : null;
};

const resolveMediaUrl = (url) => (
  url?.startsWith('/assets/')
    ? url
    : url?.startsWith('/')
    ? `${process.env.REACT_APP_BACKEND_URL}${url}`
    : url
);

const extractInviteToken = (value) => {
  const candidate = (value || '').trim();
  if (!candidate) return '';
  try {
    const parsed = new URL(candidate, window.location.origin);
    return parsed.searchParams.get('t') || parsed.searchParams.get('token') || (parsed.pathname === '/invite' ? '' : candidate);
  } catch (_error) {
    return candidate;
  }
};

const getDashboardOnlyLink = ({ isEventManager, isTeamMember, isHotelManager, isCouple, isHost }) => {
  if (isEventManager) return { to: '/dashboard', icon: ListChecks, label: 'Dashboard' };
  if (isTeamMember) return { to: '/dashboard/tasks', icon: ListChecks, label: 'My Tasks' };
  if (isHotelManager) return { to: '/dashboard/rooms?tab=arrivals', icon: RoomBedIcon, label: 'Arrivals' };
  if (isCouple) return { to: '/couple', icon: House, label: 'Dashboard' };
  if (isHost) return { to: '/host', icon: House, label: 'Dashboard' };
  return { to: '/guest', icon: House, label: 'Dashboard' };
};

const getEventHomePath = (event, fallback = '/guest') => {
  if (event?.viewer_role === 'manager') return '/dashboard';
  if (event?.viewer_role === 'staff') return '/dashboard/tasks';
  if (event?.viewer_role === 'hotel_manager') return '/dashboard/rooms?tab=arrivals';
  if (event?.viewer_role === 'bride' || event?.viewer_role === 'groom') return '/couple';
  if (event?.viewer_role === 'host') return '/host';
  return fallback;
};

const isActiveLink = (location, to) => {
  const [pathname, search = ''] = to.split('?');
  if (location.pathname !== pathname) return false;
  if (!search) return true;
  const expectedParams = new URLSearchParams(search);
  const actualParams = new URLSearchParams(location.search);
  return Array.from(expectedParams.entries()).every(
    ([key, value]) => actualParams.get(key) === value
  );
};

const PINNED_SIDEBAR_LINKS_EVENT = 'event-manager-sidebar-pins-updated';
const SIDEBAR_SCROLL_STORAGE_PREFIX = 'galago-sidebar-scroll';
const sidebarScrollPositions = new Map();

const readSidebarScrollPosition = (key) => {
  if (!key) return 0;
  if (sidebarScrollPositions.has(key)) return sidebarScrollPositions.get(key);
  if (typeof window === 'undefined') return 0;
  try {
    const stored = Number(window.sessionStorage.getItem(`${SIDEBAR_SCROLL_STORAGE_PREFIX}:${key}`));
    const position = Number.isFinite(stored) && stored > 0 ? stored : 0;
    sidebarScrollPositions.set(key, position);
    return position;
  } catch (_error) {
    return 0;
  }
};

const saveSidebarScrollPosition = (key, position) => {
  if (!key) return;
  const normalizedPosition = Math.max(0, Number(position) || 0);
  sidebarScrollPositions.set(key, normalizedPosition);
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      `${SIDEBAR_SCROLL_STORAGE_PREFIX}:${key}`,
      String(normalizedPosition)
    );
  } catch (_error) {
    // In-memory persistence still covers route changes when storage is unavailable.
  }
};

const useEventManagerSidebarPins = (userId) => {
  const [pinnedLinkPaths, setPinnedLinkPaths] = useState(
    () => readPinnedSidebarLinks(userId, eventManagerPinPaths)
  );

  useEffect(() => {
    setPinnedLinkPaths(readPinnedSidebarLinks(userId, eventManagerPinPaths));
  }, [userId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const syncPinnedLinks = () => {
      setPinnedLinkPaths(readPinnedSidebarLinks(userId, eventManagerPinPaths));
    };

    window.addEventListener(PINNED_SIDEBAR_LINKS_EVENT, syncPinnedLinks);
    window.addEventListener('storage', syncPinnedLinks);
    return () => {
      window.removeEventListener(PINNED_SIDEBAR_LINKS_EVENT, syncPinnedLinks);
      window.removeEventListener('storage', syncPinnedLinks);
    };
  }, [userId]);

  const updatePinnedLinkPaths = (nextPinnedPaths) => {
    setPinnedLinkPaths(nextPinnedPaths);
    savePinnedSidebarLinks(userId, nextPinnedPaths);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event(PINNED_SIDEBAR_LINKS_EVENT));
    }
  };

  return { pinnedLinkPaths, updatePinnedLinkPaths };
};

const useScrollOverflowIndicator = (refreshKey, persistenceKey) => {
  const scrollContainerRef = useRef(null);
  const overflowEndRef = useRef(null);
  const restoringScrollRef = useRef(false);
  const lastKnownScrollTopRef = useRef(readSidebarScrollPosition(persistenceKey));
  const [hasMoreBelow, setHasMoreBelow] = useState(true);

  const updateOverflowIndicator = useCallback(() => {
    const element = scrollContainerRef.current;
    if (!element || element.clientHeight <= 0) return;

    const remainingScroll = element.scrollHeight - element.scrollTop - element.clientHeight;
    setHasMoreBelow(remainingScroll > 4);
  }, []);

  const handleScroll = useCallback(() => {
    const element = scrollContainerRef.current;
    if (element && !restoringScrollRef.current) {
      lastKnownScrollTopRef.current = element.scrollTop;
      saveSidebarScrollPosition(persistenceKey, lastKnownScrollTopRef.current);
    }
    updateOverflowIndicator();
  }, [persistenceKey, updateOverflowIndicator]);

  const preserveScrollPosition = useCallback(() => {
    const element = scrollContainerRef.current;
    if (element) lastKnownScrollTopRef.current = element.scrollTop;
    saveSidebarScrollPosition(persistenceKey, lastKnownScrollTopRef.current);
  }, [persistenceKey]);

  useLayoutEffect(() => {
    const element = scrollContainerRef.current;
    const overflowEnd = overflowEndRef.current;
    if (!element) return undefined;

    let animationFrame;
    let followUpFrame;
    let restoreAnimationFrame;
    let restoreFollowUpFrame;
    let disposed = false;
    const savedScrollTop = readSidebarScrollPosition(persistenceKey);
    lastKnownScrollTopRef.current = savedScrollTop;
    restoringScrollRef.current = true;
    const scheduleOverflowUpdate = () => {
      window.cancelAnimationFrame(animationFrame);
      window.cancelAnimationFrame(followUpFrame);
      animationFrame = window.requestAnimationFrame(() => {
        updateOverflowIndicator();
        followUpFrame = window.requestAnimationFrame(updateOverflowIndicator);
      });
    };

    element.scrollTop = savedScrollTop;
    restoreAnimationFrame = window.requestAnimationFrame(() => {
      element.scrollTop = savedScrollTop;
      updateOverflowIndicator();
      restoreFollowUpFrame = window.requestAnimationFrame(() => {
        element.scrollTop = savedScrollTop;
        restoringScrollRef.current = false;
        updateOverflowIndicator();
      });
    });
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(scheduleOverflowUpdate)
      : null;
    const mutationObserver = typeof MutationObserver !== 'undefined'
      ? new MutationObserver(scheduleOverflowUpdate)
      : null;
    const intersectionObserver = (
      typeof IntersectionObserver !== 'undefined' && overflowEnd
    )
      ? new IntersectionObserver(
          ([entry]) => {
            if (element.clientHeight <= 0) return;
            const isOverflowing = element.scrollHeight - element.clientHeight > 4;
            setHasMoreBelow(isOverflowing && !entry.isIntersecting);
          },
          {
            root: element,
            threshold: 0.99,
          }
        )
      : null;

    resizeObserver?.observe(element);
    Array.from(element.children).forEach((child) => resizeObserver?.observe(child));
    intersectionObserver?.observe(overflowEnd);
    mutationObserver?.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
    });
    document.fonts?.ready.then(() => {
      if (!disposed) scheduleOverflowUpdate();
    });
    window.addEventListener('load', scheduleOverflowUpdate);
    window.addEventListener('resize', scheduleOverflowUpdate);

    return () => {
      disposed = true;
      saveSidebarScrollPosition(persistenceKey, lastKnownScrollTopRef.current);
      restoringScrollRef.current = false;
      window.cancelAnimationFrame(animationFrame);
      window.cancelAnimationFrame(followUpFrame);
      window.cancelAnimationFrame(restoreAnimationFrame);
      window.cancelAnimationFrame(restoreFollowUpFrame);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      intersectionObserver?.disconnect();
      window.removeEventListener('load', scheduleOverflowUpdate);
      window.removeEventListener('resize', scheduleOverflowUpdate);
    };
  }, [persistenceKey, refreshKey, updateOverflowIndicator]);

  const scrollForMore = () => {
    scrollContainerRef.current?.scrollBy({ top: 180, behavior: 'smooth' });
  };

  return {
    scrollContainerRef,
    overflowEndRef,
    hasMoreBelow,
    scrollForMore,
    handleScroll,
    preserveScrollPosition,
    updateOverflowIndicator,
  };
};

const ScrollForMoreIndicator = ({ dark = false, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'absolute inset-x-0 bottom-0 z-10 flex h-12 items-end justify-center gap-1 pb-2 text-[0.625rem] font-semibold uppercase tracking-[0.14em]',
      dark
        ? 'bg-gradient-to-t from-[var(--theme-sidebar)] via-[var(--theme-sidebar)] to-transparent text-[var(--theme-sidebar-text)]'
        : 'bg-gradient-to-t from-background via-background/95 to-transparent text-muted-foreground'
    )}
    aria-label="Scroll to see more navigation options"
  >
    <CaretDown className="h-3.5 w-3.5 animate-bounce" weight="bold" />
  </button>
);

const LinkEventDialog = ({
  open,
  onOpenChange,
  eventCode,
  onEventCodeChange,
  onSubmit,
  isSubmitting
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Join Another Event</DialogTitle>
        <DialogDescription>
          Enter an event code to join another event.
        </DialogDescription>
      </DialogHeader>
      <form
        onSubmit={onSubmit}
        className="space-y-4"
      >
        <div className="space-y-2">
          <Label htmlFor="event-link-code">Invite Link Or Token</Label>
          <Input
            id="event-link-code"
            value={eventCode}
            onChange={(e) => onEventCodeChange(e.target.value)}
            placeholder="Paste invite URL or token"
            data-testid="link-event-code-input"
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="submit"
            className="bg-wedding-sage hover:bg-wedding-sage/90"
            disabled={isSubmitting}
            data-testid="link-event-btn"
          >
            {isSubmitting ? 'Joining...' : 'Join Another Event'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
);

const formatLiveLocationTime = (value) => {
  if (!value) return 'just now';
  try {
    return new Intl.DateTimeFormat('en-IN', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(new Date(value));
  } catch (_error) {
    return 'just now';
  }
};

const formatMobileEventDate = (value) => {
  if (!value) return 'Date to be announced';
  try {
    return new Intl.DateTimeFormat('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(value));
  } catch (_error) {
    return 'Date to be announced';
  }
};

const formatMobileEventDateRange = (startValue, endValue) => {
  const startLabel = formatMobileEventDate(startValue);
  if (!endValue) return startLabel;

  const startDate = new Date(startValue);
  const endDate = new Date(endValue);
  if (
    Number.isNaN(startDate.getTime())
    || Number.isNaN(endDate.getTime())
    || startDate.toDateString() === endDate.toDateString()
  ) {
    return startLabel;
  }

  return `${startLabel} – ${formatMobileEventDate(endValue)}`;
};

const ManagerLiveLocationFloatingViewer = () => {
  const {
    currentEvent,
    isBride,
    isGroom,
    isHost,
    isAdminGuest,
    isEventGuestAdmin,
  } = useAuth();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const [liveLocation, setLiveLocation] = useState(null);
  const [open, setOpen] = useState(false);
  const canViewManagerLocation = eventTypeConfig.features.showManagerLocation
    && isWithinEventPeriod(currentEvent)
    && Boolean(isBride || isGroom || isHost || isAdminGuest || isEventGuestAdmin);
  const eventId = currentEvent?.id;
  const isActive = canViewManagerLocation && Boolean(liveLocation?.enabled);
  const mapUrl = liveLocation?.lat != null && liveLocation?.lng != null
    ? `https://maps.google.com/maps?q=${liveLocation.lat},${liveLocation.lng}&z=16&output=embed`
    : null;

  const fetchLiveLocation = useCallback(async () => {
    if (!eventId || !canViewManagerLocation) {
      setLiveLocation(null);
      setOpen(false);
      return;
    }

    try {
      const response = await api.get(`/events/${eventId}/manager-location`);
      setLiveLocation(response.data?.enabled ? response.data : null);
      if (!response.data?.enabled) {
        setOpen(false);
      }
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('Failed to fetch manager live location:', error);
      }
      setLiveLocation(null);
      setOpen(false);
    }
  }, [canViewManagerLocation, eventId]);

  useEffect(() => {
    fetchLiveLocation();
    if (!eventId || !canViewManagerLocation) return undefined;

    const interval = window.setInterval(fetchLiveLocation, 45000);
    return () => window.clearInterval(interval);
  }, [canViewManagerLocation, eventId, fetchLiveLocation]);

  if (!isActive) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom))] right-4 z-50 grid h-14 w-14 place-items-center rounded-full border border-emerald-200 bg-emerald-600 text-white shadow-[0_18px_45px_rgba(22,101,52,0.35)] transition hover:-translate-y-0.5 hover:bg-emerald-700 md:bottom-6 md:right-6"
        aria-label="Open event manager live location"
        title="Event manager live location"
        data-testid="manager-live-location-floating-button"
      >
        <span className="absolute -right-0.5 -top-0.5 h-4 w-4 rounded-full border-2 border-white bg-red-500" />
        <UserCircle className="h-8 w-8" weight="fill" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl overflow-hidden p-0">
          <div className="border-b bg-white p-5">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCircle className="h-5 w-5 text-emerald-700" weight="fill" />
                Event manager live location
              </DialogTitle>
              <DialogDescription>
                {liveLocation.shared_by_name || 'The event manager'} is sharing their current position.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="space-y-4 p-5">
            {mapUrl && (
              <div className="overflow-hidden rounded-lg border bg-muted">
                <iframe
                  title="Event manager live location map"
                  src={mapUrl}
                  className="h-72 w-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            )}

            <div className="grid gap-3 text-sm text-muted-foreground sm:grid-cols-2">
              <div className="flex items-center gap-2 rounded-lg border bg-card p-3">
                <Clock className="h-4 w-4 text-emerald-700" />
                Updated {formatLiveLocationTime(liveLocation.updated_at)}
              </div>
              <div className="flex items-center gap-2 rounded-lg border bg-card p-3">
                <MapPin className="h-4 w-4 text-emerald-700" />
                Accuracy {Math.round(liveLocation.accuracy || 0) || 'not available'} m
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
              {liveLocation.maps_url && (
                <Button type="button" variant="outline" asChild>
                  <a href={liveLocation.maps_url} target="_blank" rel="noopener noreferrer">
                    <MapPin className="mr-2 h-4 w-4" />
                    Open map
                  </a>
                </Button>
              )}
              {liveLocation.directions_url && (
                <Button type="button" className="bg-emerald-700 hover:bg-emerald-800" asChild>
                  <a href={liveLocation.directions_url} target="_blank" rel="noopener noreferrer">
                    <NavigationArrow className="mr-2 h-4 w-4" weight="fill" />
                    Get directions
                  </a>
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const Sidebar = () => {
  const { user, currentEvent, logout, isEventManager, isTeamMember, isHotelManager, isCouple, isHost, switchEvent, claimInvite, hasAdminAccess, canCreateEvents, canJoinEvents } = useAuth();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const location = useLocation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [eventCode, setEventCode] = useState('');
  const [isLinkingEvent, setIsLinkingEvent] = useState(false);
  const { pinnedLinkPaths, updatePinnedLinkPaths } = useEventManagerSidebarPins(user?.id);
  const dashboardHome = getDashboardOnlyLink({ isEventManager, isTeamMember, isHotelManager, isCouple, isHost }).to;

  const fullLinks = isEventManager
    ? eventManagerLinks
    : isTeamMember
      ? teamMemberLinks
    : isHotelManager
      ? hotelManagerLinks
    : isCouple
      ? coupleLinks
    : isHost
      ? hostLinks
      : hasAdminAccess
        ? adminGuestLinks
        : guestLinks;
  const links = currentEvent
    ? filterLinksByEventType(fullLinks, eventTypeConfig)
    : [getDashboardOnlyLink({ isEventManager, isTeamMember, isHotelManager, isCouple, isHost })];
  const navigationLinks = [
    ...links,
    ...(!isHotelManager && !links.some((link) => link.to === settingsNavLink.to)
      ? [settingsNavLink]
      : []),
  ];
  const orderedNavigationLinks = isEventManager
    ? orderSidebarLinks(navigationLinks, pinnedLinkPaths)
    : navigationLinks;
  const sidebarScrollKey = `desktop:${user?.id || 'anonymous'}:${currentEvent?.id || 'no-event'}:${dashboardHome}`;
  const {
    scrollContainerRef,
    overflowEndRef,
    hasMoreBelow,
    scrollForMore,
    handleScroll,
    preserveScrollPosition,
  } = useScrollOverflowIndicator(
    orderedNavigationLinks.map((link) => link.to).join('|'),
    sidebarScrollKey
  );

  // Get the appropriate logo - couple logo first, then branding logo
  const managerBranding = getManagerBranding(currentEvent, user, isEventManager);
  
  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchEvents();
    }
  }, [user?.id, currentEvent?.id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleSwitchEvent = async (eventId) => {
    const nextEvent = await switchEvent(eventId);
    navigate(getEventHomePath(nextEvent, dashboardHome));
  };

  const handleLinkAnotherEvent = async (e) => {
    e?.preventDefault();
    const token = extractInviteToken(eventCode);
    if (!token) {
      toast.error('Please paste an invite link or token');
      return;
    }
    try {
      setIsLinkingEvent(true);
      const response = await claimInvite(token);
      await fetchEvents();
      const nextEvent = await switchEvent(response.event_id);
      setEventCode('');
      setIsLinkDialogOpen(false);
      navigate(getEventHomePath(nextEvent, dashboardHome));
    } catch (error) {
      console.error('Failed to link event:', error);
      toast.error(error.response?.data?.detail || 'Failed to link event');
    } finally {
      setIsLinkingEvent(false);
    }
  };

  const handleTogglePin = (link) => {
    if (link.to === dashboardHome) return;

    const isPinned = pinnedLinkPaths.includes(link.to);
    if (!isPinned && pinnedLinkPaths.length >= MAX_PINNED_SIDEBAR_LINKS) {
      toast.error(`You can pin up to ${MAX_PINNED_SIDEBAR_LINKS} menu options. Unpin one first.`);
      return;
    }

    const nextPinnedPaths = isPinned
      ? pinnedLinkPaths.filter((path) => path !== link.to)
      : [...pinnedLinkPaths, link.to];

    updatePinnedLinkPaths(nextPinnedPaths);
  };

  return (
    <aside 
      className="fixed left-0 top-0 z-40 hidden h-screen w-60 flex-col border-r border-[color:var(--theme-sidebar-border)] bg-[var(--theme-sidebar)] text-[var(--theme-sidebar-text)] md:flex"
      data-testid="desktop-sidebar"
    >
      {/* The active event is the primary workspace identity. */}
      <div className="border-b border-[color:var(--theme-sidebar-border)] p-3">
        <div className="flex items-center gap-2 rounded-md border border-[color:var(--theme-sidebar-border)] bg-[var(--theme-sidebar-raised)] p-2 transition-colors hover:bg-[var(--theme-sidebar-hover)]">
          <Link
            to="/home"
            className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-md bg-wedding-gold text-wedding-charcoal transition-transform hover:scale-[1.03]"
            aria-label="Go to home page"
            title="Home"
            data-testid="desktop-home-link"
          >
            <House className="h-5 w-5" weight="fill" />
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex min-w-0 flex-1 items-center gap-2 text-left" data-testid="event-switcher">
                <div className="flex-1 min-w-0">
                  <h1 className="truncate text-sm font-semibold leading-tight text-white">
                    {currentEvent?.name || 'Select Event'}
                  </h1>
                  <p className="mt-1 text-[0.625rem] text-[var(--theme-sidebar-muted)]">{eventTypeConfig.label} workspace</p>
                </div>
                <CaretUpDown className="h-4 w-4 flex-shrink-0 text-[var(--theme-sidebar-muted)]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {events.map((event) => (
                <DropdownMenuItem 
                  key={event.id}
                  onClick={() => handleSwitchEvent(event.id)}
                  className="flex items-center gap-2"
                  data-testid={`switch-to-${event.id}`}
                >
                  {event.id === currentEvent?.id && <Check className="w-4 h-4 text-wedding-sage" />}
                  <span className={event.id === currentEvent?.id ? 'font-medium' : ''}>
                    {event.name}
                    {event.viewer_role && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {formatViewerRole(event.viewer_role)}
                      </span>
                    )}
                  </span>
                </DropdownMenuItem>
              ))}
              {canCreateEvents && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard/events/new" className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Create New Event
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
              {canJoinEvents && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setIsLinkDialogOpen(true)} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Join Another Event
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <LinkEventDialog
        open={isLinkDialogOpen}
        onOpenChange={setIsLinkDialogOpen}
        eventCode={eventCode}
        onEventCodeChange={setEventCode}
        onSubmit={handleLinkAnotherEvent}
        isSubmitting={isLinkingEvent}
      />

      {/* Navigation */}
      <div className="relative min-h-0 flex-1">
        <nav
          ref={scrollContainerRef}
          onScroll={handleScroll}
          className="h-full space-y-1 overflow-y-auto p-3 pb-12"
        >
          {orderedNavigationLinks.map((link) => {
            const isActive = isActiveLink(location, link.to);
            const isPinned = pinnedLinkPaths.includes(link.to);
            const testIdLabel = link.label.toLowerCase().replace(/\s+/g, '-');
            return (
              <div
                key={link.to}
                className={cn(
                  'group flex items-center rounded-md text-sm font-medium transition-all duration-200',
                  isActive
                    ? 'bg-[var(--theme-sidebar-active)] text-[var(--theme-sidebar-active-text)]'
                    : 'text-[var(--theme-sidebar-muted)] hover:bg-[var(--theme-sidebar-hover)] hover:text-white'
                )}
              >
                <Link
                  to={link.to}
                  onClick={preserveScrollPosition}
                  data-testid={`nav-${testIdLabel}`}
                  className="flex min-w-0 flex-1 items-center gap-3 px-3 py-2.5"
                >
                  <link.icon className="h-5 w-5 flex-shrink-0" weight={isActive ? 'fill' : 'regular'} />
                  <span className="truncate">{link.label}</span>
                </Link>
                {isEventManager && !['/home', '/dashboard'].includes(link.to) && (
                  <button
                    type="button"
                    onClick={() => handleTogglePin(link)}
                    className={cn(
                      'mr-2 grid h-7 w-7 flex-shrink-0 place-items-center rounded transition-colors',
                      isPinned
                        ? 'text-wedding-gold hover:bg-black/10'
                        : isActive
                          ? 'text-[var(--theme-sidebar-active-text)] hover:bg-black/10'
                          : 'text-[var(--theme-sidebar-muted)] hover:bg-white/10 hover:text-white'
                    )}
                    aria-label={`${isPinned ? 'Unpin' : 'Pin'} ${link.label}`}
                    aria-pressed={isPinned}
                    title={`${isPinned ? 'Unpin' : 'Pin'} ${link.label}`}
                    data-testid={`pin-nav-${testIdLabel}`}
                  >
                    {isPinned ? (
                      <PushPinSimple className="h-4 w-4" weight="fill" />
                    ) : (
                      <PushPin className="h-4 w-4" weight="regular" />
                    )}
                  </button>
                )}
              </div>
            );
          })}
          <span ref={overflowEndRef} className="block h-px w-full" aria-hidden="true" />
        </nav>
        {hasMoreBelow && (
          <ScrollForMoreIndicator dark onClick={scrollForMore} />
        )}
      </div>

      {/* Company Branding - For guests/couple viewing event manager's branding */}
      {managerBranding?.company_name && (
        <div className="border-t border-[color:var(--theme-sidebar-border)] bg-[var(--theme-sidebar-raised)] px-4 py-3">
          <div className="grid grid-cols-[auto_1fr] items-center gap-x-2 gap-y-1">
            {managerBranding.company_logo && (
              <img 
                src={resolveMediaUrl(managerBranding.company_logo)}
                alt=""
                className="row-span-2 h-7 w-7 rounded bg-white object-contain"
              />
            )}
            <span className="text-[0.625rem] leading-none text-[var(--theme-sidebar-muted)]">Managed by</span>
            {managerBranding.company_website ? (
              <a 
                href={managerBranding.company_website}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate text-xs font-medium leading-tight text-[var(--theme-sidebar-text)] hover:underline"
              >
                {managerBranding.company_name}
              </a>
            ) : (
              <span className="truncate text-xs font-medium leading-tight text-[var(--theme-sidebar-text)]">
                {managerBranding.company_name}
              </span>
            )}
          </div>
        </div>
      )}

      {/* User section */}
      <div className="border-t border-[color:var(--theme-sidebar-border)] p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button 
              className="flex w-full items-center gap-3 rounded-md p-2 transition-colors hover:bg-[var(--theme-sidebar-hover)]"
              data-testid="user-menu-trigger"
            >
              <div className="flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--theme-sidebar-border)] bg-[var(--theme-sidebar-raised)]">
                <span className="text-sm font-semibold text-[var(--theme-sidebar-text)]">
                  {user?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 text-left">
                <p className="truncate text-sm font-medium text-white">{user?.name}</p>
                <p className="text-[0.625rem] capitalize text-[var(--theme-sidebar-muted)]">{user?.role?.replace('_', ' ')}</p>
              </div>
              <CaretDown className="h-4 w-4 text-[var(--theme-sidebar-muted)]" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
              <SignOut className="w-4 h-4 mr-2" />
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Link to={dashboardHome} className="flex items-center gap-2 border-t border-[color:var(--theme-sidebar-border)] bg-[var(--theme-sidebar-raised)] px-4 py-3 text-[var(--theme-sidebar-text)]">
        <span className="grid h-8 w-8 place-items-center overflow-hidden rounded-md bg-[#edf2ef]">
          <img src="/assets/logo.png" alt="" className="h-7 w-7 object-contain" />
        </span>
        <span className="flex min-w-0 flex-col">
          <strong className="text-xs leading-tight">GalaGo</strong>
          <small className="mt-0.5 text-[0.5625rem] text-[var(--theme-sidebar-muted)]">Events on autopilot</small>
        </span>
      </Link>
    </aside>
  );
};

export const MobileNav = () => {
  const { user, currentEvent, logout, isEventManager, isTeamMember, isHotelManager, isCouple, isHost, switchEvent, claimInvite, hasAdminAccess, canCreateEvents, canJoinEvents } = useAuth();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const location = useLocation();
  const isAccountPage = location.pathname.startsWith('/account/');
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventSwitcherOpen, setEventSwitcherOpen] = useState(false);
  const [eventHeaderCompact, setEventHeaderCompact] = useState(false);
  const eventHeaderScrollFrameRef = useRef(null);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [eventCode, setEventCode] = useState('');
  const [isLinkingEvent, setIsLinkingEvent] = useState(false);
  const [showGuestSwitchHint, setShowGuestSwitchHint] = useState(false);
  const [pendingBottomPath, setPendingBottomPath] = useState('');
  const guestSwitchHintShownRef = useRef(false);
  const { pinnedLinkPaths, updatePinnedLinkPaths } = useEventManagerSidebarPins(user?.id);
  const dashboardHome = getDashboardOnlyLink({ isEventManager, isTeamMember, isHotelManager, isCouple, isHost }).to;
  const isGuestExperience = !isEventManager && !isTeamMember && !isHotelManager && !isCouple && !isHost;

  const fullLinks = isEventManager
    ? eventManagerLinks
    : isTeamMember
      ? teamMemberLinks
    : isHotelManager
      ? hotelManagerLinks
    : isCouple
      ? coupleLinks
    : isHost
      ? hostLinks
      : hasAdminAccess
        ? adminGuestLinks
        : guestLinks;
  const links = currentEvent
    ? filterLinksByEventType(fullLinks, eventTypeConfig)
    : [
        getDashboardOnlyLink({ isEventManager, isTeamMember, isHotelManager, isCouple, isHost }),
        ...(!isHotelManager ? [settingsNavLink] : [])
      ];
  const navigationLinks = [
    ...links,
    ...(!isHotelManager && !links.some((link) => link.to === settingsNavLink.to)
      ? [settingsNavLink]
      : []),
  ];
  const orderedNavigationLinks = isEventManager
    ? orderSidebarLinks(navigationLinks, pinnedLinkPaths)
    : navigationLinks;
  const bottomBarLinks = orderedNavigationLinks.slice(0, 5);
  const sidebarScrollKey = `mobile:${user?.id || 'anonymous'}:${currentEvent?.id || 'no-event'}:${dashboardHome}`;
  const {
    scrollContainerRef,
    overflowEndRef,
    hasMoreBelow,
    scrollForMore,
    handleScroll,
    preserveScrollPosition,
  } = useScrollOverflowIndicator(
    `${open}:${events.length}:${orderedNavigationLinks.map((link) => link.to).join('|')}`,
    sidebarScrollKey
  );

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchEvents();
    }
  }, [user?.id, currentEvent?.id]);

  useEffect(() => {
    setPendingBottomPath('');
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isAccountPage && (!isEventManager || !currentEvent?.id)) {
      setEventHeaderCompact(false);
      return undefined;
    }

    const updateHeaderSize = () => {
      if (eventHeaderScrollFrameRef.current !== null) return;
      eventHeaderScrollFrameRef.current = window.requestAnimationFrame(() => {
        eventHeaderScrollFrameRef.current = null;
        setEventHeaderCompact((current) => (
          current ? window.scrollY > 8 : window.scrollY > 120
        ));
      });
    };
    updateHeaderSize();
    window.addEventListener('scroll', updateHeaderSize, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateHeaderSize);
      if (eventHeaderScrollFrameRef.current !== null) {
        window.cancelAnimationFrame(eventHeaderScrollFrameRef.current);
        eventHeaderScrollFrameRef.current = null;
      }
    };
  }, [currentEvent?.id, isAccountPage, isEventManager]);

  useEffect(() => {
    if (location.pathname !== '/guest') {
      setShowGuestSwitchHint(false);
      return undefined;
    }
    if (!isGuestExperience || events.length < 2 || !user?.id || guestSwitchHintShownRef.current) return undefined;
    guestSwitchHintShownRef.current = true;
    setShowGuestSwitchHint(true);
    const timer = window.setTimeout(() => setShowGuestSwitchHint(false), 4500);
    return () => window.clearTimeout(timer);
  }, [events.length, isGuestExperience, location.pathname, user?.id]);

  const handleLogout = () => {
    logout();
    navigate('/login');
    setOpen(false);
  };

  const handleSwitchEvent = async (eventId) => {
    const nextEvent = await switchEvent(eventId);
    setOpen(false);
    navigate(getEventHomePath(nextEvent, dashboardHome));
  };

  const handleLinkAnotherEvent = async (e) => {
    e?.preventDefault();
    const token = extractInviteToken(eventCode);
    if (!token) {
      toast.error('Please paste an invite link or token');
      return;
    }

    try {
      setIsLinkingEvent(true);
      const response = await claimInvite(token);
      await fetchEvents();
      const nextEvent = await switchEvent(response.event_id);
      setEventCode('');
      setIsLinkDialogOpen(false);
      navigate(getEventHomePath(nextEvent, dashboardHome));
    } catch (error) {
      console.error('Failed to link event:', error);
      toast.error(error.response?.data?.detail || 'Failed to link event');
    } finally {
      setIsLinkingEvent(false);
    }
  };

  const handleTogglePin = (link) => {
    if (link.to === dashboardHome) return;

    const isPinned = pinnedLinkPaths.includes(link.to);
    if (!isPinned && pinnedLinkPaths.length >= MAX_PINNED_SIDEBAR_LINKS) {
      toast.error(`You can pin up to ${MAX_PINNED_SIDEBAR_LINKS} menu options. Unpin one first.`);
      return;
    }

    updatePinnedLinkPaths(
      isPinned
        ? pinnedLinkPaths.filter((path) => path !== link.to)
        : [...pinnedLinkPaths, link.to]
    );
  };

  const hasBrandedMobileHeader = Boolean(isAccountPage || (isEventManager && currentEvent?.id));
  const expandedEventHeader = Boolean(hasBrandedMobileHeader && !eventHeaderCompact);
  const mobileHeaderTitle = isAccountPage ? 'GalaGo' : (currentEvent?.name || 'GalaGo');
  const eventHeaderBackPath = location.pathname === '/dashboard' ? '/home' : '/dashboard';
  const eventHeaderBackLabel = location.pathname === '/dashboard'
    ? 'Back to Home'
    : 'Back to Dashboard';
  const eventNameLength = mobileHeaderTitle.trim().length;
  const expandedEventNameClass = eventNameLength > 34
    ? 'text-[1.25rem] leading-[1.12] line-clamp-3'
    : eventNameLength > 22
      ? 'text-[1.5rem] leading-[1.1] line-clamp-3'
      : 'text-[1.9rem] leading-[1.08] line-clamp-2';
  const expandedEventHeaderHeight = isAccountPage
    ? 'calc(6.5rem + env(safe-area-inset-top))'
    : eventNameLength > 34
      ? 'calc(8.5rem + env(safe-area-inset-top))'
      : eventNameLength > 22
        ? 'calc(7.5rem + env(safe-area-inset-top))'
        : 'calc(6.5rem + env(safe-area-inset-top))';

  return (
    <div className="sticky top-0 z-50 md:hidden">
      {/* Top bar */}
      <div
        className={cn(
          'relative flex flex-col items-stretch justify-center gap-1 border-b bg-background/85 px-3 backdrop-blur-xl transition-[height,box-shadow] duration-200 ease-out',
          hasBrandedMobileHeader && 'border-[#294b40]',
          expandedEventHeader
            ? 'mobile-event-header--inverted pt-[env(safe-area-inset-top)] shadow-[0_18px_38px_rgba(21,54,44,0.18)]'
            : hasBrandedMobileHeader
              ? 'rounded-none pb-2 pt-[calc(0.5rem+env(safe-area-inset-top))]'
              : 'rounded-none pt-[env(safe-area-inset-top)]'
        )}
        style={hasBrandedMobileHeader ? {
          background: 'radial-gradient(circle at 86% 12%, rgba(236, 193, 108, 0.3), transparent 27%), radial-gradient(circle at 68% 110%, rgba(116, 177, 150, 0.25), transparent 34%), linear-gradient(125deg, #12372c 0%, #245b49 55%, #183f34 100%)',
          height: expandedEventHeader ? expandedEventHeaderHeight : 'calc(5.25rem + env(safe-area-inset-top))',
        } : { height: 'calc(4rem + env(safe-area-inset-top))' }}
      >
        {hasBrandedMobileHeader && !isAccountPage && (
          <div className="flex items-center gap-1.5 text-[#b8cbc4]">
            <Link
              to={eventHeaderBackPath}
              className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-white hover:bg-white/10"
              aria-label={eventHeaderBackLabel}
            >
              <ArrowLeft className="h-4 w-4" />
            </Link>
            {!isAccountPage && (
              <span className="truncate text-xs font-semibold uppercase tracking-[0.06em]">
                {formatMobileEventDateRange(currentEvent.start_date, currentEvent.end_date)}
              </span>
            )}
          </div>
        )}
        <div className="flex w-full items-center gap-2">
        {isAccountPage && (
          <Link
            to="/home"
            className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-white hover:bg-white/10"
            aria-label="Back to Home"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
        )}
        <Link
          to={isAccountPage ? '/home' : dashboardHome}
          className={cn('flex shrink-0 items-center', hasBrandedMobileHeader && 'rounded-xl bg-white/90 p-1')}
          aria-label="Home"
        >
          {!isAccountPage && (currentEvent?.branding?.couple_logo_url || currentEvent?.branding?.logo_url) ? (
            <img 
              src={currentEvent?.branding?.couple_logo_url || currentEvent?.branding?.logo_url}
              alt={currentEvent?.name || 'Event'} 
              className="h-8 w-8 object-contain rounded"
            />
          ) : (
            <img src="/assets/logo.png" alt="GalaGo logo" className="h-8 w-8 object-contain rounded" />
          )}
        </Link>
        <div className="min-w-0 flex-1">
          <strong
            className={cn(
              'block font-serif font-semibold leading-tight text-wedding-charcoal transition-[font-size] duration-300',
              hasBrandedMobileHeader && 'text-white',
              !isAccountPage && 'relative top-[2px]',
              expandedEventHeader
                ? `whitespace-normal break-words ${expandedEventNameClass}`
                : isAccountPage
                  ? 'truncate text-[1.55rem] leading-tight'
                  : 'truncate text-[1.4rem] leading-tight'
            )}
          >
            {mobileHeaderTitle}
          </strong>
        </div>

        {!isAccountPage && isEventManager && currentEvent?.id && (
          <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-white hover:bg-white/10 hover:text-white" asChild>
            <Link to="/dashboard?action=edit-event" aria-label="Edit event details">
              <Pencil style={{ width: 20, height: 20 }} weight="bold" />
            </Link>
          </Button>
        )}

        {!isAccountPage && (
        <Sheet open={open} onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) setEventSwitcherOpen(false);
        }}>
          <div className="relative">
            {showGuestSwitchHint && (
              <div
                role="status"
                className="pointer-events-none absolute right-0 top-11 w-52 rounded-lg bg-wedding-charcoal px-3 py-2 text-xs leading-5 text-white shadow-xl animate-in fade-in-0 slide-in-from-top-1"
                data-testid="guest-mobile-event-switch-hint"
              >
                More invitations available. Switch events from this menu.
                <span className="absolute -top-1.5 right-3 h-3 w-3 rotate-45 bg-wedding-charcoal" />
              </div>
            )}
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                data-testid="mobile-menu-trigger"
                className={cn(
                  'h-10 w-10',
                  isEventManager && currentEvent?.id && 'text-white hover:bg-white/10 hover:text-white',
                  showGuestSwitchHint && 'ring-2 ring-wedding-gold ring-offset-2 animate-pulse'
                )}
                onClick={() => setShowGuestSwitchHint(false)}
              >
                <List style={{ width: 22, height: 22 }} weight="bold" />
              </Button>
            </SheetTrigger>
          </div>
          <SheetContent side="right" className="flex h-[100dvh] w-72 flex-col gap-0 overflow-hidden p-0">
            <div className="flex flex-shrink-0 items-center justify-between border-b p-4 pr-12 pt-[calc(1rem+env(safe-area-inset-top))]">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-wedding-sage/20 flex items-center justify-center">
                  <span className="text-sm font-semibold text-wedding-sage">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role?.replace('_', ' ')}</p>
                </div>
              </div>
            </div>

            <div className="relative min-h-0 flex-1">
              <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="h-full overflow-y-auto overscroll-contain pb-12"
              >
                {/* Event Switcher for Mobile */}
                <div className="border-b p-4">
                    <button
                      type="button"
                      className="flex w-full items-center justify-between rounded-md px-2 py-2 text-left text-sm font-medium text-wedding-charcoal hover:bg-gray-100"
                      onClick={() => setEventSwitcherOpen((current) => !current)}
                      aria-expanded={eventSwitcherOpen}
                    >
                      <span>{events.length > 0 ? 'Switch event' : 'Select event'}</span>
                      <CaretDown className={cn('h-4 w-4 transition-transform', eventSwitcherOpen && 'rotate-180')} />
                    </button>
                    {eventSwitcherOpen && (
                    <div className="space-y-1">
                      {events.map((event) => (
                        <button
                          key={event.id}
                          onClick={() => handleSwitchEvent(event.id)}
                          className={cn(
                            'w-full rounded-md px-3 py-2 text-left text-sm',
                            event.id === currentEvent?.id
                              ? 'bg-wedding-sage/10 font-medium text-wedding-sage'
                              : 'hover:bg-gray-100'
                          )}
                        >
                          <span>{event.name}</span>
                          {event.viewer_role && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {formatViewerRole(event.viewer_role)}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                    )}
                  </div>

                <nav className="space-y-1 p-4">
                  {orderedNavigationLinks.map((link) => {
                    const isActive = isActiveLink(location, link.to);
                    const isPinned = pinnedLinkPaths.includes(link.to);
                    const testIdLabel = link.label.toLowerCase().replace(/\s+/g, '-');
                    return (
                      <div
                        key={link.to}
                        className={cn(
                          'flex items-center rounded-md text-sm font-medium transition-colors',
                          isActive
                            ? 'bg-wedding-sage text-white'
                            : 'text-wedding-charcoal/70 hover:bg-wedding-sage/10'
                        )}
                      >
                        <Link
                          to={link.to}
                          onClick={() => {
                            preserveScrollPosition();
                            setOpen(false);
                          }}
                          className="flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5"
                        >
                          <link.icon className="h-5 w-5 flex-shrink-0" weight={isActive ? 'fill' : 'regular'} />
                          <span className="truncate">{link.label}</span>
                        </Link>
                        {isEventManager && !['/home', '/dashboard'].includes(link.to) && (
                          <button
                            type="button"
                            onClick={() => handleTogglePin(link)}
                            className={cn(
                              'mr-2 grid h-8 w-8 flex-shrink-0 place-items-center rounded transition-colors',
                              isPinned
                                ? isActive
                                  ? 'text-white hover:bg-white/15'
                                  : 'text-[#b27a16] hover:bg-wedding-sage/10'
                                : isActive
                                  ? 'text-white/75 hover:bg-white/15 hover:text-white'
                                  : 'text-muted-foreground hover:bg-wedding-sage/10 hover:text-wedding-sage'
                            )}
                            aria-label={`${isPinned ? 'Unpin' : 'Pin'} ${link.label}`}
                            aria-pressed={isPinned}
                            title={`${isPinned ? 'Unpin' : 'Pin'} ${link.label}`}
                            data-testid={`mobile-pin-nav-${testIdLabel}`}
                          >
                            {isPinned ? (
                              <PushPinSimple className="h-4 w-4" weight="fill" />
                            ) : (
                              <PushPin className="h-4 w-4" weight="regular" />
                            )}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </nav>
                <span ref={overflowEndRef} className="block h-px w-full" aria-hidden="true" />
              </div>
              {hasMoreBelow && (
                <ScrollForMoreIndicator onClick={scrollForMore} />
              )}
            </div>

            <div className="flex-shrink-0 border-t p-4 pb-[calc(1rem+env(safe-area-inset-bottom))]">
              {canCreateEvents && (
                <Button variant="ghost" className="w-full justify-start" asChild>
                  <Link to="/dashboard/events/new" onClick={() => setOpen(false)}>
                    <Plus className="mr-2 h-5 w-5 text-wedding-sage" />
                    Create New Event
                  </Link>
                </Button>
              )}
              {canJoinEvents && (
                <Button
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => {
                    setOpen(false);
                    setIsLinkDialogOpen(true);
                  }}
                >
                  <Plus className="mr-2 h-5 w-5 text-wedding-sage" />
                  Join Another Event
                </Button>
              )}
              <Button
                variant="ghost"
                className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleLogout}
              >
                <SignOut className="w-5 h-5 mr-2" />
                Logout
              </Button>
            </div>
          </SheetContent>
        </Sheet>
        )}
        </div>
      </div>
      <LinkEventDialog
        open={isLinkDialogOpen}
        onOpenChange={setIsLinkDialogOpen}
        eventCode={eventCode}
        onEventCodeChange={setEventCode}
        onSubmit={handleLinkAnotherEvent}
        isSubmitting={isLinkingEvent}
      />

      {/* Bottom tab bar */}
      <nav className={cn(
        'fixed bottom-0 left-0 right-0 h-[calc(4.75rem+env(safe-area-inset-bottom))] items-center justify-around border-t bg-background/95 pb-[env(safe-area-inset-bottom)] z-50',
        isGuestExperience || isAccountPage ? 'hidden' : 'flex'
      )}>
        {bottomBarLinks.map((link) => {
          const isActive = pendingBottomPath
            ? pendingBottomPath === link.to
            : isActiveLink(location, link.to);
          return (
            <Link
              key={link.to}
              to={link.to}
              onClick={() => setPendingBottomPath(link.to)}
              className={cn(
                'mx-1 flex min-w-0 flex-1 flex-col items-center gap-1 rounded-xl px-1 py-2 transition-[background-color,color,transform] duration-200 ease-out active:scale-[0.97]',
                isActive
                  ? 'bg-wedding-sage/10 text-wedding-sage'
                  : 'text-muted-foreground hover:bg-wedding-sage/5'
              )}
            >
              <link.icon
                className={cn('h-6 w-6 transition-transform duration-200 ease-out', isActive && '-translate-y-0.5 scale-105')}
                weight={isActive ? 'fill' : 'regular'}
              />
              <span className={cn('w-full truncate text-center text-xs transition-[font-weight] duration-200', isActive ? 'font-semibold' : 'font-medium')}>
                {link.label}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
};

const GuestDesktopEventMenu = () => {
  const { user, currentEvent, logout, switchEvent, claimInvite, canCreateEvents, canJoinEvents } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [eventCode, setEventCode] = useState('');
  const [isLinkingEvent, setIsLinkingEvent] = useState(false);
  const [showSwitchHint, setShowSwitchHint] = useState(false);
  const switchHintShownRef = useRef(false);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/events');
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    }
  };

  useEffect(() => {
    if (user?.id) {
      fetchEvents();
    }
  }, [user?.id, currentEvent?.id]);

  useEffect(() => {
    if (location.pathname !== '/guest') {
      setShowSwitchHint(false);
      return undefined;
    }
    if (events.length < 2 || !user?.id || switchHintShownRef.current) return undefined;
    switchHintShownRef.current = true;
    setShowSwitchHint(true);
    const timer = window.setTimeout(() => setShowSwitchHint(false), 4500);
    return () => window.clearTimeout(timer);
  }, [events.length, location.pathname, user?.id]);

  const handleSwitchEvent = async (eventId) => {
    const nextEvent = await switchEvent(eventId);
    navigate(getEventHomePath(nextEvent, '/guest'));
  };

  const handleJoinAnotherEvent = async (e) => {
    e?.preventDefault();
    const token = extractInviteToken(eventCode);
    if (!token) {
      toast.error('Please paste an invite link or token');
      return;
    }

    try {
      setIsLinkingEvent(true);
      const response = await claimInvite(token);
      await fetchEvents();
      const nextEvent = await switchEvent(response.event_id);
      setEventCode('');
      setIsLinkDialogOpen(false);
      navigate(getEventHomePath(nextEvent, '/guest'));
    } catch (error) {
      console.error('Failed to join event:', error);
      toast.error(error.response?.data?.detail || 'Failed to join event');
    } finally {
      setIsLinkingEvent(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="fixed right-5 top-5 z-50 hidden md:block">
      {showSwitchHint && (
        <div
          role="status"
          className="pointer-events-none absolute right-0 top-14 w-56 rounded-lg bg-wedding-charcoal px-3 py-2 text-xs leading-5 text-white shadow-xl animate-in fade-in-0 slide-in-from-top-1"
          data-testid="guest-desktop-event-switch-hint"
        >
          You have more invitations. Switch events here anytime.
          <span className="absolute -top-1.5 right-5 h-3 w-3 rotate-45 bg-wedding-charcoal" />
        </div>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex h-12 items-center gap-1.5 rounded-full border border-border bg-card/85 px-2.5 shadow-[0_12px_32px_rgba(0,0,0,0.12)] backdrop-blur-xl transition hover:bg-card',
              showSwitchHint && 'ring-2 ring-wedding-gold ring-offset-2 animate-pulse'
            )}
            data-testid="guest-desktop-event-switcher"
            aria-label="Switch event"
            title="Switch event"
            onClick={() => setShowSwitchHint(false)}
          >
            <img src="/assets/logo.png" alt="GalaGo" className="h-8 w-8 flex-shrink-0 rounded-full object-contain" />
            <ArrowsLeftRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" weight="bold" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {events.map((event) => (
            <DropdownMenuItem
              key={event.id}
              onClick={() => handleSwitchEvent(event.id)}
              className="flex items-center gap-2"
              data-testid={`guest-switch-to-${event.id}`}
            >
              {event.id === currentEvent?.id && <Check className="h-4 w-4 text-wedding-sage" />}
              <span className={cn('min-w-0 flex-1 truncate', event.id === currentEvent?.id && 'font-medium')}>
                {event.name}
              </span>
              {event.viewer_role && (
                <span className="text-xs text-muted-foreground">
                  {formatViewerRole(event.viewer_role)}
                </span>
              )}
            </DropdownMenuItem>
          ))}
          {canCreateEvents && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem asChild>
                <Link to="/dashboard/events/new" className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create New Event
                </Link>
              </DropdownMenuItem>
            </>
          )}
          {canJoinEvents && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setIsLinkDialogOpen(true)} className="flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Join Another Event
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 text-destructive focus:text-destructive">
            <SignOut className="h-4 w-4" />
            Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <LinkEventDialog
        open={isLinkDialogOpen}
        onOpenChange={setIsLinkDialogOpen}
        eventCode={eventCode}
        onEventCodeChange={setEventCode}
        onSubmit={handleJoinAnotherEvent}
        isSubmitting={isLinkingEvent}
      />
    </div>
  );
};

export const DashboardLayout = ({ children, variant = 'default' }) => {
  const { user, currentEvent, isEventManager, isTeamMember, isHotelManager, isCouple, isHost } = useAuth();
  const { theme, hasCustomTheme, isThemeEnabled } = useEventTheme();
  const eventTypeConfig = useEventTypeConfig(currentEvent);
  const location = useLocation();
  const navigate = useNavigate();
  const isGuestExperience = !isEventManager && !isTeamMember && !isHotelManager && !isCouple && !isHost;
  
  const themeColors = theme.colors;
  const themeFonts = theme.fonts;
  const themeImage = theme.themeImage;
  
  // Get manager branding
  const managerBranding = getManagerBranding(currentEvent, user, isEventManager);
  const guestFooterLinks = filterLinksByEventType(
    guestLinks.filter((link) => link.to !== '/guest'),
    eventTypeConfig
  ).slice(0, 5);
  const eventHostNames = eventTypeConfig.isWedding
    ? [currentEvent?.bride_name, currentEvent?.groom_name].filter(Boolean).join(' & ')
    : eventTypeConfig.isBirthday
      ? (currentEvent?.honoree_names || []).join(' & ')
    : (currentEvent?.host_names || []).join(' & ');
  const isCommandLayout = variant === 'command' && !isGuestExperience;
  const guestManagerCreditRef = useRef(null);
  const [showFixedManagerCredit, setShowFixedManagerCredit] = useState(false);

  useEffect(() => {
    if (!isGuestExperience || !managerBranding?.company_name) {
      setShowFixedManagerCredit(false);
      return undefined;
    }

    setShowFixedManagerCredit(true);
    const credit = guestManagerCreditRef.current;
    if (!credit || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => setShowFixedManagerCredit(!entry.isIntersecting),
      { threshold: 0.05 }
    );
    observer.observe(credit);
    return () => observer.disconnect();
  }, [isGuestExperience, managerBranding?.company_name]);
  
  return (
    <div 
      className="min-h-screen flex flex-col"
      data-event-theme={isThemeEnabled ? 'active' : 'inactive'}
      style={{
        backgroundColor: isCommandLayout ? '#f1f2ef' : (hasCustomTheme ? themeColors.background : '#FAF9F6'),
        fontFamily: isCommandLayout ? 'Manrope, ui-sans-serif, system-ui, sans-serif' : (hasCustomTheme ? themeFonts.body : undefined),
        '--mobile-app-header-height': isEventManager && currentEvent?.id ? '5.25rem' : '4rem',
      }}
    >
      {/* Theme Background Overlay */}
      {themeImage && (
        <div 
          className="fixed inset-0 pointer-events-none z-0 opacity-25"
          data-testid="event-theme-background"
          style={{
            backgroundImage: `url(${resolveMediaUrl(themeImage)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
      )}
      
      {/* Event Logo Watermark */}
      {currentEvent?.branding?.couple_logo_url && !isEventManager && (
        <div 
          className="fixed bottom-4 right-4 opacity-10 pointer-events-none z-0"
          style={{ width: '120px', height: '120px' }}
        >
          <img 
            src={currentEvent.branding.couple_logo_url}
            alt=""
            className="w-full h-full object-contain"
          />
        </div>
      )}
      
      {!isGuestExperience && <Sidebar />}
      <MobileNav />
      {isGuestExperience && <GuestDesktopEventMenu />}
      <main className={cn(
        'flex-1 relative z-10',
        isGuestExperience
          ? (location.pathname === '/guest' ? 'pb-0' : 'pb-8')
          : 'pb-[calc(5rem+env(safe-area-inset-bottom))] md:ml-60 md:pb-0'
      )}>
        <div className={cn(
          isCommandLayout
            ? 'p-0'
            : (isGuestExperience && location.pathname === '/guest'
              ? 'px-4 pt-4 md:px-6 md:pt-6 lg:px-8 lg:pt-8'
              : 'p-4 md:p-6 lg:p-8'),
          isGuestExperience && 'mx-auto w-full max-w-7xl'
        )}>
          {isGuestExperience && location.pathname !== '/guest' && (
            <div className="mb-4 md:mb-6 animate-in fade-in-0 slide-in-from-top-2 duration-500">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-full border border-white/60 bg-white/70 px-3 py-2 shadow-sm backdrop-blur-md">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate('/guest')}
                  className="rounded-full px-3 text-wedding-charcoal hover:bg-white/80"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back To Home
                </Button>
                <div className="min-w-0 text-right">
                  <p className="truncate text-xs uppercase tracking-[0.24em] text-muted-foreground">
                    {eventHostNames || currentEvent?.name || eventTypeConfig.labels.guestHomeTitleFallback}
                  </p>
                  <p className="truncate text-sm font-medium text-wedding-charcoal">
                    {location.pathname === '/guest' ? 'Event Home' : currentEvent?.name}
                  </p>
                </div>
              </div>
            </div>
          )}
          {children}
        </div>
      </main>
      <ManagerLiveLocationFloatingViewer />
      <ChatWidget />
      
      {/* Footer with Company Branding */}
      <footer className={cn(
        'relative z-10',
        !isGuestExperience && 'md:ml-60',
        isCommandLayout && 'hidden'
      )}>
        {isGuestExperience ? (
          <div className="relative overflow-hidden bg-[var(--theme-sidebar)] text-white shadow-[0_-18px_45px_rgba(0,0,0,0.12)]">
            <div className="h-1 w-full bg-gradient-to-r from-transparent via-wedding-gold to-transparent" />
            <div className="pointer-events-none absolute -bottom-36 -left-24 h-80 w-80 rounded-full border border-white/[0.04]" />
            <div className="pointer-events-none absolute -bottom-24 -left-12 h-60 w-60 rounded-full border border-white/[0.05]" />
            <div className="relative mx-auto grid w-full max-w-7xl gap-10 px-6 py-12 md:grid-cols-[minmax(0,1fr)_12rem_minmax(0,1fr)] md:items-center md:px-8 md:py-14">
              <div className="max-w-xl">
                <div className="flex h-11 w-11 items-center justify-center rounded-full border border-wedding-gold/40 text-wedding-gold">
                  <HeartStraight className="h-5 w-5" weight="fill" />
                </div>
                <p className="mt-5 text-[0.625rem] uppercase tracking-[0.36em] text-white/40">
                  {eventTypeConfig.labels.guestFooterEyebrow}
                </p>
                <h3 className="mt-3 font-serif text-3xl text-white md:text-4xl">
                  {eventHostNames || currentEvent?.name || eventTypeConfig.labels.guestHomeTitleFallback}
                </h3>
                <p className="mt-3 max-w-md text-sm leading-6 text-white/55">
                  {eventTypeConfig.labels.guestFooterDescription}
                </p>
              </div>
              <div className="flex flex-col items-center justify-center">
                <div className="flex w-full items-center gap-3">
                  <span className="h-px flex-1 bg-gradient-to-r from-transparent to-wedding-gold/35" />
                  <span className="h-1.5 w-1.5 rotate-45 bg-wedding-gold/70" />
                  <span className="h-px flex-1 bg-gradient-to-l from-transparent to-wedding-gold/35" />
                </div>
                <img
                  src="/assets/ganesh.png"
                  alt="Lord Ganesh"
                  className="my-3 h-36 w-auto opacity-90 drop-shadow-[0_10px_24px_rgba(0,0,0,0.18)] md:h-40"
                />
                <p className="text-center text-[0.5625rem] uppercase tracking-[0.32em] text-wedding-gold/55">
                  Auspicious beginnings
                </p>
              </div>
              <div className="md:text-right">
                <p className="text-[0.625rem] uppercase tracking-[0.3em] text-white/35 md:text-right">
                  Continue exploring
                </p>
                <div className="mt-4 flex max-w-md flex-wrap gap-x-6 gap-y-3 md:justify-end">
                  {guestFooterLinks.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="group inline-flex items-center gap-2 text-sm text-white/65 transition-colors hover:text-white"
                    >
                      <span className="h-1 w-1 rotate-45 bg-wedding-gold transition-transform group-hover:scale-150" />
                      {link.label}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
            {managerBranding?.company_name && (
              <div ref={guestManagerCreditRef} className="border-t border-white/[0.07] bg-black/10 px-6 py-4">
                <div className="mx-auto flex w-full max-w-7xl items-center justify-center gap-2">
                  <span className="text-xs uppercase tracking-[0.18em] text-white/30">Managed by</span>
                  {managerBranding.company_logo && (
                    <img 
                      src={resolveMediaUrl(managerBranding.company_logo)}
                      alt={managerBranding.company_name}
                      className="h-5 w-5 rounded object-contain"
                    />
                  )}
                  {managerBranding.company_website ? (
                    <a 
                      href={managerBranding.company_website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium text-white/65 hover:text-white hover:underline"
                      data-testid="company-branding-link"
                    >
                      {managerBranding.company_name}
                    </a>
                  ) : (
                    <span className="text-sm font-medium text-white/65">
                      {managerBranding.company_name}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : managerBranding?.company_name ? (
          <div className="py-4 px-6 border-t bg-white/50 backdrop-blur-sm">
            <div className="flex items-center justify-center gap-2">
              <span className="text-sm text-muted-foreground">Managed by</span>
              {managerBranding.company_logo && (
                <img 
                  src={resolveMediaUrl(managerBranding.company_logo)}
                  alt={managerBranding.company_name}
                  className="w-5 h-5 object-contain"
                />
              )}
              {managerBranding.company_website ? (
                <a 
                  href={managerBranding.company_website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-wedding-sage hover:underline"
                  data-testid="company-branding-link"
                >
                  {managerBranding.company_name}
                </a>
              ) : (
                <span className="text-sm font-medium text-wedding-sage">
                  {managerBranding.company_name}
                </span>
              )}
            </div>
          </div>
        ) : null}
      </footer>
      {isGuestExperience && managerBranding?.company_name && showFixedManagerCredit && (
        <div
          className="fixed inset-x-0 bottom-0 z-40 border-t border-white/10 bg-[var(--theme-sidebar)] px-4 py-2 text-white shadow-[0_-8px_24px_rgba(0,0,0,0.22)] animate-in fade-in-0 slide-in-from-bottom-2"
          data-testid="guest-fixed-manager-credit"
        >
          <div className="mx-auto flex max-w-7xl items-center justify-center gap-2">
            <span className="text-[0.625rem] uppercase tracking-[0.18em] text-white/45">Managed by</span>
            {managerBranding.company_logo && (
              <img
                src={resolveMediaUrl(managerBranding.company_logo)}
                alt=""
                className="h-4 w-4 rounded object-contain"
              />
            )}
            {managerBranding.company_website ? (
              <a
                href={managerBranding.company_website}
                target="_blank"
                rel="noopener noreferrer"
                className="truncate text-xs font-semibold text-white/80 hover:text-white hover:underline"
              >
                {managerBranding.company_name}
              </a>
            ) : (
              <span className="truncate text-xs font-semibold text-white/80">
                {managerBranding.company_name}
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
